//tasks-page.tsx

import {
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";
import { tasksApi } from "../services/api/tasks";
import { DateTime, DateTimeFormatOptions, Duration } from "luxon";
import { CompletedTask, TaskType, Tasks } from "../models/tasks";
import {
  Typography,
  TextField,
  IconButton,
  Button,
  Box,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import { useLayoutEffect } from "react";
import { DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { Autocomplete } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Size } from "../models/size";
import { sizesApi } from "../services/api/sizes";
import { unitSystemLabel, unitSystemLabelOther } from "./crops-page";
import { UserContext, ErrorContext } from "../components/app-routes";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Crop } from "../models/crop";
import { cropsApi } from "../services/api/crops";
import {
  CropTask,
  HarvestTask,
  PackageTask,
  SoakTask,
  SowTask,
  Task,
} from "../models/task";
import mixpanel from "mixpanel-browser";
import { UpgradePlanDialog } from "../components/upgrade-plan-dialog";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Tooltip } from "../components/tooltip";
import { Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { validationMessages } from "../utils/validation/messages";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmDialog from "../components/confirm-dialog";
import { ozToGrams } from "../components/crop-form";
import humanizeDuration from "humanize-duration";
import { Field, FieldArray, FastField, FieldProps } from "formik";
import { AppTextField } from "../components/text-field";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { GridColDef } from "@mui/x-data-grid";
import { TasksWeekView } from "../components/tasks-week-view";
import TasksDaySoakSection from "../components/tasks/tasks-day-soak-section";
import TasksDaySowSection from "../components/tasks/tasks-day-sow-section";
import TasksDayBlackoutSection from "../components/tasks/tasks-day-blackout-section";
import TasksDayUncoverSection from "../components/tasks/tasks-day-uncover-section";
import TasksDayHarvestSection from "../components/tasks/tasks-day-harvest-section";
import TasksDayPackageSection from "../components/tasks/tasks-day-package-section";
import {
  SoakTaskRow,
  SowTaskRow,
  BlackoutTaskRow,
  UncoverTaskRow,
  HarvestTaskRow,
  PackageTaskRow,
} from "../types/task-rows";
import ResponsiveActionBar from "../components/responsive-action-bar";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getBatchColor } from "../utils/batchColor";
import { usersApi } from "../services/api/users";
import ArchiveIcon from "@mui/icons-material/Archive";
import { GridRenderCellParams } from "@mui/x-data-grid";
import Portal from "@mui/material/Portal";
import { sameHarvestKey } from "../models/tasks";
import { getCustomLabel } from "../utils/custom-labels";

type TasksRowBase = {
  cropId: number;
  harvestDate: string;
};

const alpha = (a: any, b: any) =>
  String(a.cropName ?? "").localeCompare(String(b.cropName ?? ""), undefined, {
    sensitivity: "base",
  });

const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: "shortEn",
  languages: {
    shortEn: {
      y: () => "y",
      mo: () => "mo",
      w: () => "w",
      d: () => "d",
      h: () => "h",
      m: () => "m",
      s: () => "s",
      ms: () => "ms",
    },
  },
});

type GrowerUser = {
  id: number;
  Name: string;
};

interface TaskDialogFormValues {
  date: DateTime | null;
  seedLots: string[];
  pkgLots: string[];
  completedBy: string;
  notes: string;
  traysActual?: number;
  yieldActual?: number;
  packagesActual?: number;
}

const taskDialogFormSchema = yup.object({
  date: yup
    .date()
    .required(validationMessages.Required)
    .typeError(validationMessages.Date),
  seedLots: yup.array().of(yup.number()),
  pkgLots: yup.array().of(yup.number()),
  completedBy: yup.string().typeError(validationMessages.String),
  notes: yup.string(),
  traysActual: yup
    .number()
    .nullable()
    .min(0, validationMessages.Min)
    .integer(validationMessages.Integer)
    .typeError(validationMessages.Number),
  yieldActual: yup
    .number()
    .nullable()
    .min(0, validationMessages.Min)
    .typeError(validationMessages.Number),
  packagesActual: yup
    .number()
    .nullable()
    .min(0, validationMessages.Min)
    .typeError(validationMessages.Number),
});

const taskLastCompletedByStorageKey = "taskLastCompletedBy";

function getTaskDialogSubTitle(taskDialogData: {
  task: SowTask | SoakTask | CropTask | HarvestTask | PackageTask;
  crop: Crop | null;
  type: TaskType;
  completedTask?: CompletedTask;
}): string {
  let subTitle =
    taskDialogData.type.charAt(0).toUpperCase() + taskDialogData.type.slice(1);

  if (taskDialogData.type === "package") {
    subTitle += ` ${(taskDialogData.task as PackageTask).product.name}`;
  } else {
    subTitle += ` ${taskDialogData.crop!.name}`;
  }

  return subTitle;
}

export default function TasksPage() {
  const isMobile = useMediaQuery("(max-width:600px)");

  const dialogContainer =
    typeof window !== "undefined" ? window.document.body : undefined;
  const [loadingOtherData, setLoadingOtherData] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [periodTasks, setPeriodTasks] = useState<Tasks[] | undefined>(
    undefined,
  );
  const [periodTasksTag, setPeriodTasksTag] = useState(0);
  const [sizes, setSizes] = useState<Size[] | undefined>(undefined);
  const [crops, setCrops] = useState<Crop[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [openUpgradePlanDialog, setOpenUpgradePlanDialog] = useState(false);
  const [openCompleteAllTasksDialog, setOpenCompleteAllTasksDialog] =
    useState(false);
  const [completeAllTasksResult, setCompleteAllTasksResult] = useState<{
    completed: number;
    blocked: number;
  } | null>(null);
  const context = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);
  const grower = context.grower;
  const location = useLocation();
  const history = useHistory();
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [taskDialogData, setTaskDialogData] = useState<{
    task: SowTask | SoakTask | CropTask | HarvestTask | PackageTask;
    crop: Crop | null;
    type: TaskType;
    completedTask?: CompletedTask;
  } | null>(null);
  const [users, setUsers] = useState<GrowerUser[]>([]);
  const [openUncompleteTaskPrompt, setOpenUncompleteTaskPrompt] =
    useState(false);
  const [uncompletingTask, setUncompletingTask] = useState<Task | undefined>(
    undefined,
  );
  const [uncompletingTaskType, setUncompletingTaskType] =
    useState<TaskType | null>(null);
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const formikRef = useRef<FormikProps<TaskDialogFormValues>>(null);
  const [taskBlockDialogOpen, setTaskBlockDialogOpen] = useState(false);
  const [taskBlockData, setTaskBlockData] = useState<{
    missingTasks: string[];
    firstMissing: string;
  } | null>(null);
  const [blockedTaskObject, setBlockedTaskObject] = useState<{
    task: CropTask;
    crop: Crop;
  } | null>(null);
  const scrollToTask = (id: string | number) => {
    document.getElementById(`task-${id}`)?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "cropName",
    "status",
    "sowDateActual",
    "sowDateExpected",
    "harvestDateActual",
    "harvestDateProjected",
    "trays",
    "yield",
    "available",
  ]);

  const toISODate = (d?: DateTime | null): string | undefined =>
    d && DateTime.isDateTime(d) ? d.toISODate()! : undefined;

  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const [readyToShow, setReadyToShow] = useState(false);

  const defaultCompletedBy = useMemo(() => {
    const last = localStorage.getItem(taskLastCompletedByStorageKey);
    if (!last) return "";

    const exists = users.some(
      (u) => u.Name.toLowerCase() === last.toLowerCase(),
    );

    return exists ? last : "";
  }, [users]);

  const getOverdueStatus = (taskCompleted: boolean, scheduledDate: string) => {
    const today = DateTime.now().toISODate();
    return !taskCompleted && scheduledDate < today;
  };

  if (!grower) {
    throw new Error("Invalid context");
  }

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  useLayoutEffect(() => {
    if (
      sessionStorage.getItem("scrollToTopOnLoad") === "true" &&
      searchParams.get("date") &&
      searchParams.get("by")
    ) {
      sessionStorage.removeItem("scrollToTopOnLoad");

      console.log("✅ useLayoutEffect: scrolling to top");

      // Step 1: Blur active element (kills leftover focus from modals/buttons)
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Step 2: Disable scroll restoration by browser
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }

      // Step 3: Scroll to top aggressively at multiple timing points
      const scrollTop = () =>
        scrollWrapperRef.current?.scrollTo({ top: 0, behavior: "auto" });

      scrollTop(); // immediately
      requestAnimationFrame(scrollTop); // next layout
      setTimeout(scrollTop, 50); // short delay
      setTimeout(scrollTop, 250); // catch late-render content
      setTimeout(scrollTop, 500); // fallback
    }
  }, [searchParams]);

  useLayoutEffect(() => {
    const shouldScroll =
      sessionStorage.getItem("scrollToTopOnLoad") === "true" &&
      searchParams.get("date") &&
      searchParams.get("by");

    if (shouldScroll) {
      sessionStorage.removeItem("scrollToTopOnLoad");

      requestAnimationFrame(() => {
        if (scrollWrapperRef.current) {
          scrollWrapperRef.current.scrollTop = 0;
          scrollWrapperRef.current.scrollIntoView({ block: "start" });
          console.log("✅ Scrolled scrollWrapperRef to top");
        }

        setReadyToShow(true); // only show after scroll
      });
    } else {
      setReadyToShow(true); // show immediately if no scroll needed
    }
  }, [searchParams]);

  // ---------- URL Normalization (Hook-Safe, No Flicker) ----------
  useEffect(() => {
    const dateParam = searchParams.get("date");
    const byParam = searchParams.get("by");

    // Nothing to fix
    if (dateParam && byParam) return;

    const updated = new URLSearchParams(searchParams);

    if (!dateParam) {
      updated.set("date", DateTime.now().toISODate());
    }
    if (!byParam) {
      updated.set("by", "day");
    }

    // Replace URL immediately
    history.replace({ search: updated.toString() });

    // Do NOT set state here.
  }, [searchParams, history]);

  const harvestDateLocaleOptions: DateTimeFormatOptions = {
    weekday: "short", // Thu
    month: "short", // Dec
    day: "numeric", // 4   ← this is the key change!
    year: "numeric", // 2025
  };

  useEffect(() => {
    async function getData() {
      setLoadingOtherData(true);
      try {
        const [sizes, crops] = await Promise.all([
          sizesApi.getAll(),
          cropsApi.getAll(),
        ]);
        if (!isMounted.current) return;
        setSizes(sizes);
        setCrops(crops);
      } catch (error) {
        handleError(error);
      } finally {
        if (isMounted.current) setLoadingOtherData(false);
      }
    }
    getData();
  }, [handleError]);

  useEffect(() => {
    if (!users.length) return;

    const last = localStorage.getItem(taskLastCompletedByStorageKey);
    if (!last) return;

    const exists = users.some(
      (u) => u.Name.toLowerCase() === last.toLowerCase(),
    );

    if (!exists) {
      localStorage.removeItem(taskLastCompletedByStorageKey);
    }
  }, [users]);

  useEffect(() => {
    async function getData() {
      const date = searchParams.get("date");
      const periodParam = searchParams.get("by");

      if (!date) return;

      const period: "day" | "week" = periodParam === "week" ? "week" : "day";

      setLoadingTasks(true);
      try {
        const { tasksForRange, users, lastCompletedBy } = await tasksApi.getAll(
          DateTime.fromISO(date),
          period,
        );

        if (!isMounted.current) return;

        setPeriodTasks(tasksForRange);
        setUsers(users);
      } catch (error) {
        handleError(error);
      } finally {
        if (isMounted.current) setLoadingTasks(false);
      }
    }

    getData();
  }, [searchParams, periodTasksTag, handleError]);

  const handleDateChange = (date: DateTime | null) => {
    if (date === null) {
      return;
    }
    searchParams.set("date", date.toISODate()!);
    history.replace({ search: searchParams.toString() });
  };

  const handlePeriodChange = (event: any) => {
    if (grower.Plan === "hobby" && event.target.value === "week") {
      setOpenUpgradePlanDialog(true);
      return;
    }
    setPeriodTasks(undefined);
    searchParams.set("by", event.target.value);
    history.replace({ search: searchParams.toString() });
  };

  const handleClickDate = (date: string) => {
    searchParams.set("date", date);
    searchParams.set("by", "day");
    history.replace({ search: searchParams.toString() });
  };

  const handleClickToday = () => {
    searchParams.set("date", DateTime.now().toISODate());
    searchParams.set("by", "day");
    history.replace({ search: searchParams.toString() });
  };

  const getCrop = useCallback(
    (cropId: number) => {
      if (!crops) {
        throw new Error("Invalid state: crops not defined");
      }
      const crop = crops.find((c) => c.id === cropId);
      if (!crop) {
        throw new Error(`Invalid state: crop not found (${cropId})`);
      }
      return crop;
    },
    [crops],
  );

  const handleExportPDF = () => {
    async function post() {
      setBusy(true);
      try {
        const date = searchParams.get("date");
        if (!date) {
          throw new Error("Invalid search parameters");
        }
        await tasksApi.getPdf(DateTime.fromISO(date));
        mixpanel.track("Export Tasks Pdf");
      } catch (error) {
        handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    }

    if (grower.Plan === "hobby") {
      setOpenUpgradePlanDialog(true);
      return;
    }

    post();
  };

  const isByDay = () => searchParams.get("by") === "day";

  const doRequestUncompleteTask = async (task: Task) => {
    setBusy(true);
    try {
      await tasksApi.uncomplete(task.completedId);
      if (isMounted.current) setPeriodTasksTag((t) => t + 1);
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const doRequestToggleCompletedTask = async (
    task: Task,
    itemId: number,
    type: TaskType,
    values?: TaskDialogFormValues,
  ) => {
    setBusy(true);
    try {
      const isEdit =
        !!task.completedId && task.completedId > 0 && values !== undefined;

      const date =
        toISODate(values?.date) ??
        searchParams.get("date") ??
        DateTime.now().toISODate();
      if (!date) {
        throw new Error("Invalid search parameters");
      }
      const plannedDate = toISODate(values?.date) ?? date;
      const effectiveDateActual = isEdit
        ? (task.completedTask?.dateActual ?? null)
        : plannedDate;

      let traysExpected: number | undefined,
        traysNeeded: number | undefined,
        traysBuffer: number | undefined,
        yieldExpected: number | undefined,
        bufferExpected: number | undefined,
        harvestDate: string | undefined,
        cropName: string | undefined,
        cropNotes: string | undefined,
        cropSowingRate: number | undefined,
        cropBufferPercentage: number | undefined,
        cropExpectedYield: number | undefined,
        seedName: string | undefined,
        seedVariety: string | undefined,
        seedNotes: string | undefined,
        packagedProductName: string | undefined,
        packagedProductSize: string | undefined,
        packagedProductWeight: number | undefined,
        packagedQuantityExpected: number | undefined,
        packagedQuantityActual: number | undefined;

      if (grower.Workflow === "advanced") {
        if (!taskDialogData) {
          throw new Error("Invalid state");
        }

        if (type !== "package") {
          const t = task as CropTask;
          traysExpected = t.trays;
          traysNeeded = t.traysExact;
          traysBuffer = t.trays - t.traysExact;
          harvestDate = t.harvestDate;
        } else {
          const pT = task as PackageTask;
          packagedProductName = pT.product.name;
          packagedProductSize =
            pT.productSize.type === "tray" ? "Tray" : pT.productSize.size?.name;
          packagedProductWeight = pT.productSize.weight;
          packagedQuantityExpected = pT.quantity;
          packagedQuantityActual = values?.packagesActual;
          harvestDate = date;
        }

        if (type === "harvest") {
          const t = task as HarvestTask;
          yieldExpected = t.expectedYield;
          bufferExpected = t.bufferExpected;
        }

        if (type === "sow") {
          if (!taskDialogData.crop) {
            throw new Error("Invalid state");
          }
          cropName = taskDialogData.crop.name;
          cropNotes = taskDialogData.crop.notes;
          cropExpectedYield = taskDialogData.crop.cutYield;
          cropSowingRate = taskDialogData.crop.seedingRate;
          cropBufferPercentage = taskDialogData.crop.bufferPercentage;
          seedName = taskDialogData.crop.seed?.name;
          seedVariety = taskDialogData.crop.seed?.variety;
          seedNotes = taskDialogData.crop.seed?.notes;
        }
      } else {
        if (type === "package") {
          harvestDate = date;
        } else {
          const t = task as CropTask;
          harvestDate = t.harvestDate;
        }
      }

      const data: CompletedTask = {
        id: isEdit ? task.completedId : 0,

        itemId,
        date: plannedDate,
        dateActual: effectiveDateActual,

        type,
        seedLots: values?.seedLots?.map((l) => ({
          id: 0,
          seedLotId: parseInt(l),
        })),
        pkgLots: values?.pkgLots
          ?.filter((l) => l)
          .slice(0, 1)
          .map((l) => ({
            id: 0,
            pkgLotId: parseInt(l),
          })),
        completedBy: values?.completedBy,
        notes: values?.notes,
        traysExpected,
        traysActual: values?.traysActual,
        traysNeeded,
        traysBuffer,
        yieldExpected,
        yieldActual: values?.yieldActual,
        bufferExpected,
        harvestDate,
        harvestKind:
          type === "harvest"
            ? ((task as HarvestTask).harvestKind ?? "cut")
            : undefined,
        cropName,
        cropNotes,
        cropExpectedYield,
        cropSowingRate,
        cropBufferPercentage,
        seedName,
        seedVariety,
        seedNotes,
        packagedProductName,
        packagedProductSize,
        packagedProductWeight,
        packagedQuantityActual,
        packagedQuantityExpected,
      };

      if (isEdit) {
        if (!data.id || data.id === 0) {
          throw new Error(
            "Refusing to update task without a valid completedId",
          );
        }

        await tasksApi.update(data);
      } else {
        await tasksApi.complete(data);
        mixpanel.track("Complete Task");
      }

      if (isMounted.current) setPeriodTasksTag((t) => t + 1);

      if (values?.completedBy) {
        const name = values.completedBy.trim();

        const exists = users.some(
          (u) => u.Name.toLowerCase() === name.toLowerCase(),
        );

        if (!exists) {
          // Optimistically add so persistence works immediately
          setUsers((prev) => [
            ...prev,
            {
              id: -Date.now(), // temp client-only ID
              Name: name,
            },
          ]);
        }

        localStorage.setItem(taskLastCompletedByStorageKey, name);
      }
    } catch (error: any) {
      const data = error?.response?.data;

      // ✅ Always handle TASK_ORDER based on structured payload
      if (data?.error === "TASK_ORDER") {
        setTaskBlockData({
          missingTasks: data.missingTasks ?? [],
          firstMissing: data.firstMissing ?? data.missingTasks?.[0] ?? "",
        });

        // Optional: support "jump to earliest task" link
        if (type !== "package" && "cropId" in task) {
          const crop = await cropsApi.getSingle(itemId);
          setBlockedTaskObject({ task: task as CropTask, crop });
        } else {
          setBlockedTaskObject(null);
        }

        setTaskBlockDialogOpen(true);
        return;
      }

      // fallback
      const message =
        data?.message ||
        error?.message ||
        "An error occurred while completing the task.";

      handleError(new Error(message));
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const handleOnClickCropTaskCompleted = async (
    task: CropTask,
    type: TaskType,
  ) => {
    const isAdvanced = grower.Workflow === "advanced";
    const isSimple = grower.Workflow === "simple";

    //
    // 1️⃣ UNCOMPLETE LOGIC (same for both workflows)
    //
    if (task.completed) {
      if (isAdvanced) {
        setOpenUncompleteTaskPrompt(true);
        setUncompletingTask(task);
        setUncompletingTaskType(type); // ✅ store the real type from the handler
      } else {
        doRequestUncompleteTask(task);
      }
      return;
    }

    //
    // 2️⃣ SIMPLE WORKFLOW (no dialogs, no pre-check, complete immediately)
    //
    if (isSimple) {
      doRequestToggleCompletedTask(task, task.cropId, type);
      return;
    }

    //
    // 3️⃣ ADVANCED WORKFLOW (preflight order check + modal if OK)
    //
    const date = searchParams.get("date");
    if (!date) {
      // Defensive guard: should not happen thanks to URL normalization,
      // but better to fail cleanly than call the API with bad params.
      handleError(new Error("Invalid search parameters"));
      return;
    }

    setBusy(true);

    try {
      // 🔍 Preflight order-check request (no dialog yet)
      await tasksApi.complete({
        id: 0,
        itemId: task.cropId,
        date,
        type,
        dateActual: null,
        harvestDate: task.harvestDate,
      });

      // If preflight OK → open modal
      const crop = await cropsApi.getSingle(task.cropId);
      setTaskDialogData({ task, crop, type });
      setOpenTaskDialog(true);
    } catch (err: any) {
      const data = err?.response?.data;

      if (data?.error === "TASK_ORDER") {
        setTaskBlockData({
          missingTasks: data.missingTasks ?? [],
          firstMissing: data.firstMissing ?? data.missingTasks?.[0],
        });

        const crop = await cropsApi.getSingle(task.cropId);
        setBlockedTaskObject({ task, crop });

        setTaskBlockDialogOpen(true);
        if (isMounted.current) setPeriodTasksTag((t) => t + 1);
        return;
      }

      handleError(err);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const handleCompleteAllAvailableTasks = async () => {
    const day = periodTasks?.[0];
    const date = searchParams.get("date");

    if (!day || !date) {
      return;
    }

    setBusy(true);
    let completedCount = 0;
    let blockedCount = 0;

    try {
      const cropTaskGroups: {
        type: TaskType;
        tasks: CropTask[];
      }[] = [
        {
          type: "harvest",
          tasks: day.harvest,
        },
        {
          type: "uncover",
          tasks: day.uncover,
        },
        {
          type: "blackout",
          tasks: day.blackout,
        },
        {
          type: "sow",
          tasks: day.sow,
        },
        {
          type: "soak",
          tasks: day.soak,
        },
      ];

      for (const group of cropTaskGroups) {
        for (const task of group.tasks) {
          if (task.completed) {
            continue;
          }

          try {
            await tasksApi.complete({
              id: 0,
              itemId: task.cropId,
              date,
              dateActual: date,
              type: group.type,
              harvestDate: task.harvestDate,
              harvestKind:
                group.type === "harvest"
                  ? ((task as HarvestTask).harvestKind ?? "cut")
                  : undefined,
            });
            completedCount += 1;
          } catch (error: any) {
            if (error?.response?.data?.error === "TASK_ORDER") {
              blockedCount += 1;
              continue;
            }

            throw error;
          }
        }
      }

      for (const task of day.package) {
        if (task.completed) {
          continue;
        }

        const productSizeId = task.productSize?.id;

        if (!productSizeId) {
          continue;
        }

        await tasksApi.complete({
          id: 0,
          itemId: productSizeId,
          date,
          dateActual: date,
          type: "package",
          harvestDate: date,
        });
        completedCount += 1;
      }

      setOpenCompleteAllTasksDialog(false);
      setCompleteAllTasksResult({
        completed: completedCount,
        blocked: blockedCount,
      });
      setPeriodTasksTag((t) => t + 1);
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) {
        setBusy(false);
      }
    }
  };

  const handleOnClickPackageTaskCompleted = (task: PackageTask) => {
    if (task.completed) {
      if (grower.Workflow === "advanced") {
        setOpenUncompleteTaskPrompt(true);
        setUncompletingTask(task);
        setUncompletingTaskType("package"); // ✅
        return;
      }
      doRequestUncompleteTask(task);
      return;
    }

    if (grower.Workflow === "simple") {
      const psId = task.productSize?.id;
      if (!psId) {
        return; // live tray or unsaved size → nothing to toggle
      }

      doRequestToggleCompletedTask(task, psId, "package");
      return;
    }

    if (isMounted.current) {
      setTaskDialogData({
        task: task,
        crop: null,
        type: "package",
      });
      setOpenTaskDialog(true);
    }
  };

  const onClickCancelUpgradePlan = () => {
    setOpenUpgradePlanDialog(false);
  };

  const onSubmitTaskDialogForm = async (values: TaskDialogFormValues) => {
    if (!taskDialogData) {
      throw new Error("Invalid state");
    }

    setOpenTaskDialog(false);
    setBusy(true);

    try {
      // 🔹 EDIT EXISTING COMPLETED TASK
      if (taskDialogData.task.completed && taskDialogData.completedTask) {
        const updated: CompletedTask = {
          ...taskDialogData.completedTask,
          date: toISODate(values.date)!,
          completedBy: values.completedBy,
          notes: values.notes,
          traysActual: values.traysActual,
          pkgLots: values?.pkgLots
            ?.filter((l) => l)
            .slice(0, 1)
            .map((l) => ({
              id: 0,
              pkgLotId: parseInt(l),
            })),
          yieldActual: values.yieldActual,
          packagedQuantityActual: values.packagesActual,
        };
        console.log("🚨 ABOUT TO CALL tasksApi.update", updated);

        await tasksApi.update(updated);
        console.log("✅ tasksApi.update resolved");
      } else {
        // 🔹 EXISTING COMPLETE FLOW (unchanged)
        let itemId: number;

        if (taskDialogData.type === "package") {
          const psId = (taskDialogData.task as PackageTask).productSize?.id;
          if (!psId) return;
          itemId = psId;
        } else {
          itemId = taskDialogData.crop!.id;
        }

        await doRequestToggleCompletedTask(
          taskDialogData.task,
          itemId,
          taskDialogData.type,
          values,
        );
      }

      if (isMounted.current) {
        setPeriodTasksTag((t) => t + 1);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const completingTaskCropHasLots =
    taskDialogData && taskDialogData.crop
      ? taskDialogData?.crop.seed!.lots.filter((l) => l.archivedAt === null)
          .length > 0
      : null;

  const shouldShowSeedLots = () => {
    if (!taskDialogData) {
      throw new Error("Invalid state");
    }

    if (!completingTaskCropHasLots) {
      return false;
    }

    if (taskDialogData.type === "package") {
      return false;
    }

    if (!taskDialogData.crop) {
      throw new Error("Invalid state");
    }

    if (taskDialogData.type !== "soak" && taskDialogData.type !== "sow") {
      return false;
    }

    if (
      taskDialogData.type === "sow" &&
      taskDialogData.crop.soakTimeMinutes !== 0
    ) {
      return false;
    }

    return true;
  };

  const packagingPkg = () => {
    if (!taskDialogData || taskDialogData.type !== "package") {
      return null;
    }

    const task = taskDialogData.task as PackageTask;

    return task.productSize.pkg || null;
  };

  const onClickEditCompletedTask = async (
    task: SowTask | SoakTask | CropTask | HarvestTask | PackageTask,
    tasktype: TaskType,
  ) => {
    setBusy(true);

    try {
      let crop: Crop | null = null;

      if (tasktype !== "package") {
        crop = await cropsApi.getSingle((task as CropTask).cropId);
      }

      const completedTask = await tasksApi.getOne(task.completedId);

      // ✅ ONLY open dialog with existing completedTask
      setTaskDialogData({
        task,
        crop,
        type: tasktype,
        completedTask,
      });
      setOpenTaskDialog(true);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleOnCloseConfirmUncompleteTask = (confirm?: boolean) => {
    setOpenUncompleteTaskPrompt(false);
    setUncompletingTaskType(null);

    if (!confirm) return;
    if (!uncompletingTask) throw new Error("Invalid state");

    doRequestUncompleteTask(uncompletingTask);
  };

  // ========= ROW + COLUMN DEFINITIONS FOR TASKGRID (DAY VIEW) =========

  const soakRows: SoakTaskRow[] = useMemo(() => {
    if (!periodTasks?.[0] || !crops) return [];
    const { soak } = periodTasks[0];

    return soak
      .map((task) => {
        const crop = getCrop(task.cropId);
        const scheduledDate = task.sowedOn; // soak scheduled on sowedOn date

        const batch =
          task.batchNumber && task.batchNumber.trim() !== ""
            ? task.batchNumber
            : null;

        return {
          id:
            task.completedId ??
            `${task.cropId}-${task.harvestDate}-${task.harvestKind ?? "cut"}`,
          cropId: task.cropId,
          completed: task.completed,
          type: "soak" as const,
          cropName: crop.name,
          cropNotes: crop.notes,
          seedName: `${crop.seed?.variety ?? ""} ${crop.seed?.name ?? ""}`,
          sowRate: crop.seedingRate,
          sowRateImperial: ozToGrams(crop.seedingRate).toFixed(2),
          harvestKind: task.harvestKind ?? "cut",

          soakTime: shortEnglishHumanizer(
            Duration.fromObject({ minutes: crop.soakTimeMinutes }).toMillis(),
          ),

          trays: `${task.trays} (${task.traysExact.toFixed(1)})`,
          totalWeight: (task.trays * crop.seedingRate).toFixed(2),
          totalWeightImperial: (
            task.trays * ozToGrams(crop.seedingRate)
          ).toFixed(2),

          harvestDate: task.harvestDate,
          scheduledDate,
          isPastDue: getOverdueStatus(task.completed, scheduledDate),

          // 🌈 Batch information
          batchNumber: batch,

          // CSS classname for TaskGrid row coloring
          batchClass: batch
            ? `batch-${batch.replace(/[^a-zA-Z0-9]/g, "_")}`
            : null,

          // Background color itself
          "--batch-color": getBatchColor(batch),
        };
      })
      .sort(alpha);
  }, [periodTasks, crops]);

  const sowRows = useMemo(() => {
    if (!periodTasks?.[0] || !crops) return [];
    const { sow } = periodTasks[0];

    const rows = sow
      .map((task) => {
        const crop = getCrop(task.cropId);
        const scheduledDate = task.sowedOn;

        return {
          id:
            task.completedId ??
            `${task.cropId}-${task.harvestDate}-${task.harvestKind ?? "cut"}`,
          cropId: task.cropId,
          completed: task.completed,
          type: "sow" as const,
          cropName: crop.name,
          cropNotes: crop.notes,
          seedName: `${crop.seed?.variety ?? ""} ${crop.seed?.name ?? ""}`,
          harvestKind: task.harvestKind ?? "cut",

          trays:
            typeof task.traysExact === "number"
              ? `${task.trays} (${task.traysExact.toFixed(1)})`
              : String(task.trays),

          sowRate: crop.seedingRate,
          sowRateImperial: ozToGrams(crop.seedingRate).toFixed(2),

          totalWeight: task.totalSeedWeight.toFixed(2),
          totalWeightImperial: ozToGrams(task.totalSeedWeight).toFixed(2),

          harvestDate: task.harvestDate,
          scheduledDate,
          isPastDue: getOverdueStatus(task.completed, scheduledDate),

          batchNumber:
            task.batchNumber && task.batchNumber.trim() !== ""
              ? task.batchNumber
              : null,

          batchClass:
            task.batchNumber && task.batchNumber.trim() !== ""
              ? `batch-${task.batchNumber.replace(/[^a-zA-Z0-9]/g, "_")}`
              : null,

          "--batch-color": getBatchColor(task.batchNumber ?? null),
        } as SowTaskRow;
      })
      .sort(alpha);

    return rows;
  }, [periodTasks, crops]);

  const blackoutRows = useMemo(() => {
    if (!periodTasks?.[0] || !crops) return [];
    const { blackout } = periodTasks[0];

    return blackout
      .map((task) => {
        const crop = getCrop(task.cropId);

        // Blackout scheduled date:
        const scheduledDate =
          task.sowedOn && crop.germinationDays != null
            ? DateTime.fromISO(task.sowedOn)
                .plus({ days: crop.germinationDays })
                .toISODate()!
            : "";

        const batch =
          task.batchNumber && task.batchNumber.trim() !== ""
            ? task.batchNumber
            : null;

        return {
          id:
            task.completedId ??
            `${task.cropId}-${task.harvestDate}-${task.harvestKind ?? "cut"}`,
          cropId: task.cropId,
          completed: task.completed,
          type: "blackout" as const,
          cropName: crop.name,
          cropNotes: crop.notes,
          trays: task.trays,
          sowedOn: task.sowedOn,
          harvestDate: task.harvestDate,
          scheduledDate,
          isPastDue: getOverdueStatus(task.completed, scheduledDate),
          harvestKind: task.harvestKind ?? "cut",

          // 🌈 Batch fields — EXACTLY like Sow:
          batchNumber: batch,

          batchClass: batch
            ? `batch-${batch.replace(/[^a-zA-Z0-9]/g, "_")}`
            : null,

          "--batch-color": getBatchColor(batch),
        } as any; // ← Sow & Soak also use relaxed typing here
      })
      .sort(alpha);
  }, [periodTasks, crops]);

  const uncoverRows = useMemo(() => {
    if (!periodTasks?.[0] || !crops) return [];
    const { uncover } = periodTasks[0];

    return uncover
      .map((task): UncoverTaskRow => {
        const crop = getCrop(task.cropId);

        // sowedOn = backend sowedOn (do NOT recompute from harvest date)
        const sowedOn = task.sowedOn;

        // uncover scheduled date = sowedOn + germinationDays + blackoutDays
        const scheduledDate = sowedOn
          ? DateTime.fromISO(sowedOn)
              .plus({
                days: (crop.germinationDays ?? 0) + (crop.blackoutDays ?? 0),
              })
              .toISODate()!
          : "";

        return {
          id: task.completedId ?? `${task.cropId}-${task.harvestDate}`,
          cropId: task.cropId,
          completed: task.completed,
          type: "uncover",
          cropName: crop.name,
          cropNotes: crop.notes,
          trays: task.trays,
          sowedOn,
          harvestDate: task.harvestDate,
          scheduledDate,
          isPastDue: getOverdueStatus(task.completed, scheduledDate),
          harvestKind: task.harvestKind ?? "cut",

          // ✅ Add batch number (null-safe)
          batchNumber:
            task.batchNumber && task.batchNumber.trim() !== ""
              ? task.batchNumber
              : null,
          // 🔹 NEW — consistent with Sow
          batchClass:
            task.batchNumber && task.batchNumber.trim() !== ""
              ? `batch-${task.batchNumber.replace(/[^a-zA-Z0-9]/g, "_")}`
              : null,

          // 🔹 NEW — consistent with Sow
          "--batch-color": getBatchColor(task.batchNumber ?? null),
        } as any;
      })
      .sort(alpha);
  }, [periodTasks, crops]);

  const harvestRows = useMemo(() => {
    if (!periodTasks?.[0] || !crops) return [];
    const { harvest } = periodTasks[0];

    return harvest
      .map((task) => {
        const crop = getCrop(task.cropId);
        const scheduledDate = task.harvestDate ?? "";

        const qtyNeeded = task.quantityNeeded ?? 0;

        const plannedBuffer =
          typeof task.bufferExpected === "number"
            ? task.bufferExpected
            : task.expectedYield - qtyNeeded;

        const isAdvanced = grower.Workflow === "advanced";
        const actualYield = task.completedTask?.yieldActual;
        const hasActualYield = typeof actualYield === "number";

        return {
          id:
            task.completedId ??
            `${task.cropId}-${task.harvestDate}-${task.harvestKind ?? "cut"}`,
          cropId: task.cropId,
          completed: task.completed,
          type: "harvest" as const,
          harvestKind: task.harvestKind ?? "cut",
          cropName: crop.name,
          cropNotes: crop.notes,
          trays: task.trays,
          sowedOn: task.sowedOn,
          harvestDate: task.harvestDate,

          expectedYield: task.expectedYield,

          yieldActual:
            task.completed && isAdvanced && hasActualYield ? actualYield : "",

          quantityNeeded: qtyNeeded,

          bufferExpected: plannedBuffer,

          bufferActual:
            task.completed && isAdvanced && hasActualYield
              ? actualYield - qtyNeeded
              : "",

          scheduledDate,
          isPastDue: getOverdueStatus(task.completed, scheduledDate),

          batchNumber:
            task.batchNumber && task.batchNumber.trim() !== ""
              ? task.batchNumber
              : null,

          batchClass:
            task.batchNumber && task.batchNumber.trim() !== ""
              ? `batch-${task.batchNumber.replace(/[^a-zA-Z0-9]/g, "_")}`
              : null,

          "--batch-color": getBatchColor(task.batchNumber ?? null),
        } as any;
      })
      .sort(alpha);
  }, [periodTasks, crops, getCrop, grower.Workflow]);

  const packageRows = useMemo(() => {
    const day = periodTasks?.[0];
    if (!day) return [];

    const sorted = [...day.package].sort((a, b) =>
      (a.product.name ?? "").localeCompare(b.product.name ?? "", undefined, {
        sensitivity: "base",
        numeric: false,
      }),
    );

    return sorted.map((task) => {
      const scheduledDate = day.date;
      const isPastDue = getOverdueStatus(task.completed, scheduledDate);

      return {
        id: task.productSize.id,
        type: "package" as const,
        productName: task.product.name,
        productNotes: task.product.notes,
        size:
          task.productSize.type === "tray"
            ? "Tray"
            : (task.productSize.size?.name ?? ""),
        packageName: task.productSize.pkg
          ? `${task.productSize.pkg.name} - ${task.productSize.pkg.size}`
          : "",
        weight: task.productSize.weight ?? "",
        quantity: task.completed
          ? (task.completedTask?.packagedQuantityActual ?? "")
          : (task.quantity ?? ""),

        completed: task.completed,
        scheduledDate,
        isPastDue,

        // 🔥 FIX: include original task so the handler gets it
        originalTask: task,
      } as PackageTaskRow;
    });
  }, [periodTasks]);

  const soakColumns: GridColDef[] = useMemo(() => {
    const day = periodTasks?.[0];
    const allCompleted =
      !!day?.soak.length && day.soak.every((t) => t.completed);
    const goToPrevTask = (row: SoakTaskRow) => {
      const sameBatch = soakRows.filter(
        (r) => r.batchNumber === row.batchNumber,
      );
      const i = sameBatch.findIndex((r) => r.id === row.id);
      if (i > 0) {
        scrollToTask(sameBatch[i - 1].id);
      }
    };

    const goToNextTask = (row: SoakTaskRow) => {
      const sameBatch = soakRows.filter(
        (r) => r.batchNumber === row.batchNumber,
      );
      const i = sameBatch.findIndex((r) => r.id === row.id);
      if (i < sameBatch.length - 1) {
        scrollToTask(sameBatch[i + 1].id);
      }
    };

    return [
      {
        field: "completed",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Checkbox
            checked={!!params.value}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            onClick={() => {
              const t = day?.soak.find((x) => sameHarvestKey(x, params.row));
              if (t) handleOnClickCropTaskCompleted(t, "soak");
            }}
          />
        ),
      },
      {
        field: "edit",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          grower.Workflow === "advanced" &&
          !!day?.soak.find((t) => sameHarvestKey(t, params.row))?.completed ? (
            <IconButton
              onClick={() => {
                const t = day?.soak.find((x) => sameHarvestKey(x, params.row));
                if (t) onClickEditCompletedTask(t, "soak");
              }}
            >
              <EditIcon />
            </IconButton>
          ) : null,
      },
      {
        field: "prevTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: () => null, // Soak has no earlier stage
      },
      {
        field: "batchNumber",
        headerName: "Batch",
        width: 71,
        align: "center",
        headerAlign: "center",

        renderCell: (params: any) => {
          const batch = params?.row?.batchNumber;

          const display =
            batch && typeof batch === "string" && batch.trim() !== ""
              ? batch
              : "--";

          return (
            <span
              style={{
                fontSize: "0.65rem",
                lineHeight: 1.1,
                display: "inline-block",
                width: "100%",
                textAlign: "center",
              }}
            >
              {display}
            </span>
          );
        },
      },

      {
        field: "nextTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<any, TasksRowBase>) => {
          const t = day?.soak.find(
            (x) =>
              x.cropId === params.row.cropId &&
              x.harvestDate === params.row.harvestDate,
          );
          if (!t) return null;

          const crop = getCrop(params.row.cropId);

          // Soak → always goes to sow
          return (
            <div
              onClick={() =>
                navigateToTaskDay("sow", { task: t as CropTask, crop })
              }
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <ChevronRightIcon style={{ fontSize: 20 }} />
            </div>
          );
        },
      },

      {
        field: "cropName",
        headerName: "Crop",
        flex: 1,
        minWidth: 160,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const crop = getCrop(params.row.cropId);
          return <Tooltip tooltipTitle={crop.notes} text={crop.name} />;
        },
      },
      {
        field: "seedName",
        headerName: "Seed",
        flex: 1,
        minWidth: 140,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "sowRate",
        headerName: `Soak Rate (${unitSystemLabel(grower)} / tray)`,
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      ...(grower.UnitSystem === "imperial"
        ? [
            {
              field: "sowRateImperial",
              headerName: `Soak Rate (${unitSystemLabelOther(grower)} / tray)`,
              flex: 1,
              headerAlign: "center",
              align: "center",
            } as GridColDef,
          ]
        : []),
      {
        field: "soakTime",
        headerName: "Soak Time",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "trays",
        headerName: allCompleted ? "Trays Soaked" : "Trays to Soak",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "totalWeight",
        headerName: `Total Seed (${unitSystemLabel(grower)})`,
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      ...(grower.UnitSystem === "imperial"
        ? [
            {
              field: "totalWeightImperial",
              headerName: `Total Seed (${unitSystemLabelOther(grower)})`,
              flex: 1,
              headerAlign: "center",
              align: "center",
            } as GridColDef,
          ]
        : []),
      {
        field: "harvestDate",
        headerName: "For Harvest On",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as string;

          return (
            <Link to={`/home/orders?from=${value}&to=${value}`}>
              {DateTime.fromISO(value).toFormat("EEE, LLL d, yyyy")}
            </Link>
          );
        },
      },
    ];
  }, [periodTasks, grower, getCrop, soakRows]);

  const sowColumns: GridColDef[] = useMemo(() => {
    const day = periodTasks?.[0];
    const allCompleted = !!day?.sow.length && day.sow.every((t) => t.completed);

    // 🔹 Move to previous STAGE in this crop's cycle (usually → soak)
    const goToPrevStage = (row: SowTaskRow) => {
      if (!day) return;

      const t = day.sow.find(
        (x) => x.cropId === row.cropId && x.harvestDate === row.harvestDate,
      );
      if (!t) return;

      const crop = getCrop(row.cropId);

      // Only navigate to soak if this crop actually has a soak stage
      if (crop.soakTimeMinutes && crop.soakTimeMinutes > 0) {
        navigateToTaskDay("soak", { task: t as CropTask, crop });
      }
      // If no soak stage → do nothing for now (could disable icon later)
    };

    // 🔹 Move to NEXT STAGE in this crop's cycle (blackout or uncover)
    const goToNextStage = (row: SowTaskRow) => {
      if (!day) return;

      const t = day.sow.find(
        (x) => x.cropId === row.cropId && x.harvestDate === row.harvestDate,
      );
      if (!t) return;

      const crop = getCrop(row.cropId);

      // If crop has blackout days → go to blackout; otherwise go straight to uncover
      const nextType: TaskType =
        crop.blackoutDays && crop.blackoutDays > 0 ? "blackout" : "uncover";

      navigateToTaskDay(nextType, { task: t as CropTask, crop });
    };

    return [
      {
        field: "completed",
        headerName: "",
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Checkbox
            checked={!!params.value}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            onClick={() => {
              const t = day?.sow.find((x) => sameHarvestKey(x, params.row));
              if (t) handleOnClickCropTaskCompleted(t, "sow");
            }}
          />
        ),
      },
      {
        field: "edit",
        headerName: "",
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          grower.Workflow === "advanced" &&
          !!day?.sow.find((t) => sameHarvestKey(t, params.row))?.completed ? (
            <IconButton
              onClick={() => {
                const t = day?.sow.find((x) => sameHarvestKey(x, params.row));
                if (t) onClickEditCompletedTask(t, "sow");
              }}
            >
              <EditIcon />
            </IconButton>
          ) : null,
      },

      // ⬅️ PREVIOUS STAGE (uses navigateToTaskDay)
      {
        field: "prevTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => (
          <div
            onClick={() => goToPrevStage(params.row)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ChevronLeftIcon style={{ fontSize: 20 }} />
          </div>
        ),
      },

      {
        field: "batchNumber",
        headerName: "Batch",
        width: 71,
        align: "center",
        headerAlign: "center",

        renderCell: (params: any) => {
          const batch = params?.row?.batchNumber;

          const display =
            batch && typeof batch === "string" && batch.trim() !== ""
              ? batch
              : "--";

          return (
            <span
              style={{
                fontSize: "0.65rem",
                lineHeight: 1.1,
                display: "inline-block",
                width: "100%",
                textAlign: "center",
              }}
            >
              {display}
            </span>
          );
        },
      },

      // ➡️ NEXT STAGE (uses navigateToTaskDay)
      {
        field: "nextTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div
            onClick={() => goToNextStage(params.row)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ChevronRightIcon style={{ fontSize: 20 }} />
          </div>
        ),
      },

      {
        field: "cropName",
        headerName: "Crop",
        flex: 1,
        minWidth: 160,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const crop = getCrop(params.row.cropId);
          return <Tooltip tooltipTitle={crop.notes} text={crop.name} />;
        },
      },
      {
        field: "seedName",
        headerName: "Seed",
        flex: 1,
        minWidth: 140,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "trays",
        headerName: allCompleted ? "Trays Sowed" : "Trays to Sow",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "sowRate",
        headerName: `Sow Rate (${unitSystemLabel(grower)} / tray)`,
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      ...(grower.UnitSystem === "imperial"
        ? [
            {
              field: "sowRateImperial",
              headerName: `Sow Rate (${unitSystemLabelOther(grower)} / tray)`,
              flex: 1,
              headerAlign: "center",
              align: "center",
            } as GridColDef,
          ]
        : []),
      {
        field: "totalWeight",
        headerName: `Total Seed (${unitSystemLabel(grower)})`,
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      ...(grower.UnitSystem === "imperial"
        ? [
            {
              field: "totalWeightImperial",
              headerName: `Total Seed (${unitSystemLabelOther(grower)})`,
              flex: 1,
              headerAlign: "center",
              align: "center",
            } as GridColDef,
          ]
        : []),
      {
        field: "harvestDate",
        headerName: "For Harvest On",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as string;

          return (
            <Link to={`/home/orders?from=${value}&to=${value}`}>
              {DateTime.fromISO(value).toFormat("EEE, LLL d, yyyy")}
            </Link>
          );
        },
      },
    ];
  }, [periodTasks, grower, getCrop]);

  const blackoutColumns: GridColDef[] = useMemo(() => {
    const day = periodTasks?.[0];
    const allCompleted =
      !!day?.blackout.length && day.blackout.every((t) => t.completed);

    // 🔹 PREVIOUS STAGE → always go to SOW
    const goToPrevStage = (row: BlackoutTaskRow) => {
      if (!day) return;
      const t = day.blackout.find(
        (x) => x.cropId === row.cropId && x.harvestDate === row.harvestDate,
      );
      if (!t) return;

      const crop = getCrop(row.cropId);
      navigateToTaskDay("sow", { task: t as CropTask, crop });
    };

    // 🔹 NEXT STAGE → always go to UNCOVER
    const goToNextStage = (row: BlackoutTaskRow) => {
      if (!day) return;
      const t = day.blackout.find(
        (x) => x.cropId === row.cropId && x.harvestDate === row.harvestDate,
      );
      if (!t) return;

      const crop = getCrop(row.cropId);
      navigateToTaskDay("uncover", { task: t as CropTask, crop });
    };

    return [
      {
        field: "completed",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Checkbox
            checked={!!params.value}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            onClick={() => {
              const t = day?.blackout.find((x) =>
                sameHarvestKey(x, params.row),
              );
              if (t) handleOnClickCropTaskCompleted(t, "blackout");
            }}
          />
        ),
      },
      {
        field: "edit",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          grower.Workflow === "advanced" &&
          !!day?.blackout.find((t) => sameHarvestKey(t, params.row))
            ?.completed ? (
            <IconButton
              onClick={() => {
                const t = day?.blackout.find((x) =>
                  sameHarvestKey(x, params.row),
                );
                if (t) onClickEditCompletedTask(t, "blackout");
              }}
            >
              <EditIcon />
            </IconButton>
          ) : null,
      },

      // ⬅️ PREVIOUS STAGE: sow
      {
        field: "prevTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div
            onClick={() => goToPrevStage(params.row)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ChevronLeftIcon style={{ fontSize: 20 }} />
          </div>
        ),
      },

      {
        field: "batchNumber",
        headerName: "Batch",
        width: 71,
        align: "center",
        headerAlign: "center",

        renderCell: (params: any) => {
          const batch = params?.row?.batchNumber;

          const display =
            batch && typeof batch === "string" && batch.trim() !== ""
              ? batch
              : "--";

          return (
            <span
              style={{
                fontSize: "0.65rem",
                lineHeight: 1.1,
                display: "inline-block",
                width: "100%",
                textAlign: "center",
              }}
            >
              {display}
            </span>
          );
        },
      },

      // ➡️ NEXT STAGE: uncover
      {
        field: "nextTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div
            onClick={() => goToNextStage(params.row)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ChevronRightIcon style={{ fontSize: 20 }} />
          </div>
        ),
      },

      {
        field: "cropName",
        headerName: "Crop",
        flex: 1,
        minWidth: 160,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const crop = getCrop(params.row.cropId);
          return <Tooltip tooltipTitle={crop.notes} text={crop.name} />;
        },
      },
      {
        field: "trays",
        headerName: allCompleted
          ? "Trays Moved to Blackout"
          : "Trays to Move to Blackout",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "sowedOn",
        headerName: "Cycle Start Date",
        width: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) =>
          params.value ? (
            <Link to={`/home/tasks?by=day&date=${params.value}`}>
              {DateTime.fromISO(params.value as string).toFormat(
                "EEE, LLL d, yyyy",
              )}
            </Link>
          ) : (
            ""
          ),
      },
      {
        field: "harvestDate",
        headerName: "For Harvest On",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as string;

          return (
            <Link to={`/home/orders?from=${value}&to=${value}`}>
              {DateTime.fromISO(value).toFormat("EEE, LLL d, yyyy")}
            </Link>
          );
        },
      },
    ];
  }, [periodTasks, grower, getCrop]);

  const uncoverColumns: GridColDef[] = useMemo(() => {
    const day = periodTasks?.[0];
    const allCompleted =
      !!day?.uncover.length && day.uncover.every((t) => t.completed);

    // 🔹 PREVIOUS STAGE → blackout OR sow depending on crop
    const goToPrevStage = (row: UncoverTaskRow) => {
      if (!day) return;

      const t = day.uncover.find(
        (x) => x.cropId === row.cropId && x.harvestDate === row.harvestDate,
      );
      if (!t) return;

      const crop = getCrop(row.cropId);

      // If crop HAS blackout stage → go to blackout
      if (crop.blackoutDays && crop.blackoutDays > 0) {
        navigateToTaskDay("blackout", { task: t as CropTask, crop });
      } else {
        // Otherwise → go back to sow
        navigateToTaskDay("sow", { task: t as CropTask, crop });
      }
    };

    // 🔹 NEXT STAGE → harvest
    const goToNextStage = (row: UncoverTaskRow) => {
      if (!day) return;

      const t = day.uncover.find(
        (x) => x.cropId === row.cropId && x.harvestDate === row.harvestDate,
      );
      if (!t) return;

      const crop = getCrop(row.cropId);
      navigateToTaskDay("harvest", { task: t as CropTask, crop });
    };

    return [
      {
        field: "completed",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Checkbox
            checked={!!params.value}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            onClick={() => {
              const t = day?.uncover.find((x) => sameHarvestKey(x, params.row));
              if (t) handleOnClickCropTaskCompleted(t, "uncover");
            }}
          />
        ),
      },
      {
        field: "edit",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          grower.Workflow === "advanced" &&
          !!day?.uncover.find((t) => sameHarvestKey(t, params.row))
            ?.completed ? (
            <IconButton
              onClick={() => {
                const t = day?.uncover.find((x) =>
                  sameHarvestKey(x, params.row),
                );
                if (t) onClickEditCompletedTask(t, "uncover");
              }}
            >
              <EditIcon />
            </IconButton>
          ) : null,
      },
      {
        field: "prevTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div
            onClick={() => goToPrevStage(params.row)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ChevronLeftIcon style={{ fontSize: 20 }} />
          </div>
        ),
      },
      {
        field: "batchNumber",
        headerName: "Batch",
        width: 71,
        align: "center",
        headerAlign: "center",

        renderCell: (params: any) => {
          const batch = params?.row?.batchNumber;

          const display =
            batch && typeof batch === "string" && batch.trim() !== ""
              ? batch
              : "--";

          return (
            <span
              style={{
                fontSize: "0.65rem",
                lineHeight: 1.1,
                display: "inline-block",
                width: "100%",
                textAlign: "center",
              }}
            >
              {display}
            </span>
          );
        },
      },

      // ➡️ NEXT STAGE: harvest
      {
        field: "nextTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div
            onClick={() => goToNextStage(params.row)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ChevronRightIcon style={{ fontSize: 20 }} />
          </div>
        ),
      },

      {
        field: "cropName",
        headerName: "Crop",
        flex: 1,
        minWidth: 160,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const crop = getCrop(params.row.cropId);
          return <Tooltip tooltipTitle={crop.notes} text={crop.name} />;
        },
      },

      {
        field: "trays",
        headerName: allCompleted ? "Trays Uncovered" : "Trays to Uncover",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },

      {
        field: "sowedOn",
        headerName: "Cycle Start Date",
        width: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) =>
          params.value ? (
            <Link to={`/home/tasks?by=day&date=${params.value}`}>
              {DateTime.fromISO(params.value as string).toFormat(
                "EEE, LLL d, yyyy",
              )}
            </Link>
          ) : (
            ""
          ),
      },

      {
        field: "harvestDate",
        headerName: "For Harvest On",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as string;

          return (
            <Link to={`/home/orders?from=${value}&to=${value}`}>
              {DateTime.fromISO(value).toFormat("EEE, LLL d, yyyy")}
            </Link>
          );
        },
      },
    ];
  }, [periodTasks, grower, getCrop]);

  const harvestColumns: GridColDef[] = useMemo(() => {
    const day = periodTasks?.[0];

    // 🔹 PREVIOUS STAGE → always uncover
    const goToPrevStage = (row: HarvestTaskRow) => {
      if (!day) return;

      const t = day.harvest.find(
        (x) => x.cropId === row.cropId && x.harvestDate === row.harvestDate,
      );
      if (!t) return;

      const crop = getCrop(row.cropId);

      navigateToTaskDay("uncover", { task: t as CropTask, crop });
    };

    return [
      {
        field: "completed",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Checkbox
            checked={!!params.value}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            onClick={() => {
              const t = day?.harvest.find((x) => sameHarvestKey(x, params.row));
              if (t) handleOnClickCropTaskCompleted(t, "harvest");
            }}
          />
        ),
      },
      {
        field: "edit",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          grower.Workflow === "advanced" &&
          !!day?.harvest.find((t) => sameHarvestKey(t, params.row))
            ?.completed ? (
            <IconButton
              onClick={() => {
                const t = day?.harvest.find((x) =>
                  sameHarvestKey(x, params.row),
                );
                if (t) onClickEditCompletedTask(t, "harvest");
              }}
            >
              <EditIcon />
            </IconButton>
          ) : null,
      },

      // ⬅️ PREVIOUS STAGE (uncover)
      {
        field: "prevTask",
        headerName: "",
        width: 22,
        minWidth: 22,
        maxWidth: 22,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div
            onClick={() => goToPrevStage(params.row)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ChevronLeftIcon style={{ fontSize: 20 }} />
          </div>
        ),
      },

      {
        field: "batchNumber",
        headerName: "Batch",
        width: 71,
        align: "center",
        headerAlign: "center",

        renderCell: (params: any) => {
          const batch = params?.row?.batchNumber;

          const display =
            batch && typeof batch === "string" && batch.trim() !== ""
              ? batch
              : "--";

          return (
            <span
              style={{
                fontSize: "0.65rem",
                lineHeight: 1.1,
                display: "inline-block",
                width: "100%",
                textAlign: "center",
              }}
            >
              {display}
            </span>
          );
        },
      },
      {
        field: "cropName",
        headerName: "Crop",
        flex: 1,
        minWidth: 160,
        headerAlign: "left",
        align: "left",
        sortable: true,
        renderCell: (params) => {
          const crop = getCrop(params.row.cropId);

          const isLive = params.row.harvestKind === "live"; // ✅ from API row

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Tooltip tooltipTitle={crop.notes} text={crop.name} />

              <Chip
                label={isLive ? "LIVE" : "CUT"}
                size="small"
                sx={{
                  height: 16,
                  fontSize: "0.4rem",
                  fontWeight: 500,
                  px: 0.5, // tighter horizontal padding
                  "& .MuiChip-label": {
                    px: 0.5, // label padding (important)
                    lineHeight: 1,
                  },
                  minWidth: 0,
                  backgroundColor: isLive ? "#6dbc11ff" : "#7dc2f3ff",
                  color: "#fff",
                }}
              />
            </div>
          );
        },
      },
      {
        field: "trays",
        headerName: "Trays",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "expectedYield",
        headerName: `Expected Yield (${unitSystemLabel(grower)})`,
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      ...(grower.Workflow === "advanced"
        ? [
            {
              field: "yieldActual",
              headerName: `Actual Yield (${unitSystemLabel(grower)})`,
              flex: 1,
              headerAlign: "center",
              align: "center",
            } as GridColDef,
          ]
        : []),

      {
        field: "quantityNeeded",
        headerName: `Qty. Needed (${unitSystemLabel(grower)})`,
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "bufferExpected",
        headerName: `Buffer (${unitSystemLabel(grower)})`,
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) =>
          params.row.bufferActual !== ""
            ? params.row.bufferActual
            : params.value,
      },
      {
        field: "sowedOn",
        headerName: "Cycle Start Date",
        width: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams) =>
          params.value ? (
            <Link to={`/home/tasks?by=day&date=${params.value}`}>
              {DateTime.fromISO(params.value as string).toFormat(
                "EEE, LLL d, yyyy",
              )}
            </Link>
          ) : (
            ""
          ),
      },
    ];
  }, [periodTasks, getCrop, grower]);

  const packageColumns: GridColDef[] = useMemo(() => {
    const day = periodTasks?.[0];

    return [
      {
        field: "checkbox",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: any) => (
          <Checkbox
            color="primary"
            checked={!!params.row.completed}
            onClick={() =>
              handleOnClickPackageTaskCompleted(params.row.originalTask)
            }
          />
        ),
      },
      {
        field: "edit",
        headerName: "",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: any) =>
          grower.Workflow === "advanced" && params.row.completed ? (
            <IconButton
              onClick={() =>
                onClickEditCompletedTask(params.row.originalTask, "package")
              }
            >
              <EditIcon />
            </IconButton>
          ) : null,
      },
      {
        field: "productName",
        headerName: "Product",
        flex: 1,
        headerAlign: "left",
        align: "left",
        sortable: true,
        renderCell: (params: any) => (
          <Tooltip
            tooltipTitle={params.row.productNotes}
            text={params.row.productName}
          />
        ),
      },
      {
        field: "size",
        headerName: "Size",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "packageName",
        headerName: "Package",
        flex: 1,
        minWidth: 140,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "weight",
        headerName: `Weight (${unitSystemLabel(grower)})`,
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "quantity",
        headerName: day?.package?.some((t) => !t.completed)
          ? "Quantity To Pack"
          : "Quantity Packed",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
    ];
  }, [
    periodTasks,
    grower,
    handleOnClickPackageTaskCompleted,
    onClickEditCompletedTask,
  ]);

  // ========= NAVIGATION: Jump to earliest required task =========
  const navigateToTaskDay = (
    taskType: string,
    blocked: { task: CropTask; crop: Crop },
  ) => {
    const { task, crop } = blocked;

    const harvest = new Date(task.harvestDate);

    // backend logic
    const sowDate = new Date(harvest);
    sowDate.setDate(
      sowDate.getDate() - (crop.daysToMaturity - (crop.overnightSoak ? 1 : 0)),
    );

    const soakDate = new Date(harvest);
    soakDate.setDate(soakDate.getDate() - crop.daysToMaturity);

    const blackoutDate = new Date(sowDate);
    blackoutDate.setDate(blackoutDate.getDate() + crop.germinationDays);

    const uncoverDate = new Date(blackoutDate);
    uncoverDate.setDate(uncoverDate.getDate() + crop.blackoutDays);

    let target = sowDate;

    switch (taskType) {
      case "soak":
        target = soakDate;
        break;
      case "sow":
        target = sowDate;
        break;
      case "blackout":
        target = blackoutDate;
        break;
      case "uncover":
        target = uncoverDate;
        break;
      case "harvest":
        target = harvest;
        break;
    }

    const formatted = target.toISOString().slice(0, 10);

    sessionStorage.setItem("scrollToTopOnLoad", "true");
    history.push(`/home/tasks?by=day&date=${formatted}`);

    // 📌 Scroll to top in rAF loop
    let tries = 0;
    const scrollUntilTop = () => {
      if (window.scrollY === 0 || tries > 10) return;
      window.scrollTo(0, 0);
      tries++;
      requestAnimationFrame(scrollUntilTop);
    };
    requestAnimationFrame(scrollUntilTop);
  };

  // Avoid flicker: block UI until URL is valid
  if (!searchParams.get("date") || !searchParams.get("by")) {
    return null;
  }

  const handlePrev = () => {
    const date = searchParams.get("date") ?? DateTime.now().toISODate();

    if (!date) throw new Error("Invalid state");

    searchParams.set(
      "date",
      DateTime.fromISO(date)
        .minus({ days: isByDay() ? 1 : 7 })
        .toISODate()!,
    );

    history.replace({ search: searchParams.toString() });
  };

  const handleNext = () => {
    const date = searchParams.get("date");
    if (!date) throw new Error("Invalid state");

    searchParams.set(
      "date",
      DateTime.fromISO(date)
        .plus({ days: isByDay() ? 1 : 7 })
        .toISODate()!,
    );

    history.replace({ search: searchParams.toString() });
  };
  const by = searchParams.get("by") || "day";

  return (
    <>
      <Page title={getCustomLabel(grower ?? null, "tasks")}>
        {" "}
        <Box
          sx={{
            position: "sticky",
            top: { xs: 56, sm: 64 }, // if you have an AppBar
            zIndex: 200,
            backgroundColor: "white",
            pt: 2.5,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,

              // PC: left aligned
              justifyContent: "flex-start",

              // Mobile: push buttons BELOW date/select
              "@media (max-width: 600px)": {
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1.5,
              },
            }}
          ></Box>
          <ResponsiveActionBar
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,

              // PC layout — left aligned, custom order
              "> *": {
                order: 0,
              },

              // MOBILE layout — date + arrows together, dropdown moves below
              "@media (max-width: 600px)": {
                flexDirection: "column",
                alignItems: "flex-start",

                ".mobile-top-row": {
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  gap: 8,
                },

                ".mobile-bottom-row": {
                  width: "100%",
                },
              },
            }}
          >
            {/* ===== MOBILE: DATE + ARROWS ===== */}
            <Box className="mobile-top-row">
              <MobileDatePicker
                closeOnSelect
                format="LLL d, yyyy" // ✅ keep current format
                value={DateTime.fromISO(searchParams.get("date")!)}
                onChange={(date) => {
                  if (date && date.isValid) {
                    handleDateChange(date); // ✅ page-level handler
                  }
                }}
                slotProps={{
                  toolbar: { hidden: true },
                  textField: {
                    variant: "outlined",
                    inputProps: { readOnly: true },
                    sx: { width: 170 },
                  },
                }}
              />

              <IconButton
                onClick={handlePrev}
                sx={{ pl: 4 /* ← small left padding */ }}
              >
                <ArrowBackIosIcon />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{ pr: 2 /* ← small left padding */ }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>

            {/* ===== MOBILE: PERIOD SELECT below ===== */}
            <Box className="mobile-bottom-row" sx={{ display: "flex", gap: 2 }}>
              <FormControl sx={{ width: 100 }}>
                <Select value={by} onChange={handlePeriodChange}>
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                </Select>
              </FormControl>

              <Button
                color="secondary"
                variant="contained"
                onClick={handleClickToday}
              >
                Today
              </Button>

              {isByDay() && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleExportPDF}
                  endIcon={
                    grower.Plan === "hobby" ? (
                      <LockOutlinedIcon fontSize="small" />
                    ) : null
                  }
                >
                  Export PDF
                </Button>
              )}
            </Box>
          </ResponsiveActionBar>
        </Box>
        {periodTasks && sizes && crops && (
          <>
            {isByDay() ? (
              <div>
                {grower.Workflow === "simple" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      mb: 1,
                      mt: 5,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        mixpanel.track("Click Complete All Tasks", {
                          growerId: grower.id,
                          workflow: grower.Workflow,
                        });
                        setOpenCompleteAllTasksDialog(true);
                      }}
                      disabled={busy}
                    >
                      Complete All Tasks
                    </Button>
                  </Box>
                )}
                {/* ---- SOAK ---- */}
                {soakRows.length > 0 && (
                  <TasksDaySoakSection
                    title={getCustomLabel(grower ?? null, "soak")}
                    rows={soakRows}
                    soakColumns={soakColumns}
                    soakColumnOptions={[
                      { field: "batchNumber", label: "Batch" },
                      { field: "seedName", label: "Seed" },
                      { field: "sowRate", label: "Soak Rate" },
                      ...(grower.UnitSystem === "imperial"
                        ? [{ field: "sowRateImperial", label: "Soak Rate (g)" }]
                        : []),
                      { field: "soakTime", label: "Soak Time" },
                      { field: "trays", label: "Trays" },
                      { field: "totalWeight", label: "Total Seed" },
                      ...(grower.UnitSystem === "imperial"
                        ? [
                            {
                              field: "totalWeightImperial",
                              label: "Total Seed (g)",
                            },
                          ]
                        : []),
                      { field: "harvestDate", label: "For Harvest On" },
                    ]}
                    periodTasks={periodTasks}
                  />
                )}

                {/* ---- SOW ---- */}
                {sowRows.length > 0 && (
                  <TasksDaySowSection
                    title={getCustomLabel(grower ?? null, "sow")}
                    rows={sowRows}
                    sowColumns={sowColumns}
                    sowColumnOptions={[
                      { field: "batchNumber", label: "Batch" },
                      { field: "trays", label: "Trays" },
                      { field: "sowRate", label: "Sow Rate" },
                      ...(grower.UnitSystem === "imperial"
                        ? [{ field: "sowRateImperial", label: "Sow Rate (oz)" }]
                        : []),
                      { field: "totalWeight", label: "Total Seed" },
                      ...(grower.UnitSystem === "imperial"
                        ? [
                            {
                              field: "totalWeightImperial",
                              label: "Total Seed (oz)",
                            },
                          ]
                        : []),
                      { field: "harvestDate", label: "For Harvest On" },
                    ]}
                    periodTasks={periodTasks}
                  />
                )}

                {/* ---- BLACKOUT ---- */}
                {blackoutRows.length > 0 && (
                  <TasksDayBlackoutSection
                    title={getCustomLabel(grower ?? null, "blackout")}
                    rows={blackoutRows}
                    blackoutColumns={blackoutColumns}
                    blackoutColumnOptions={[
                      { field: "batchNumber", label: "Batch" },
                      { field: "trays", label: "Trays" },
                      { field: "sowedOn", label: "Cycle Start Date" },
                      { field: "harvestDate", label: "For Harvest On" },
                    ]}
                    periodTasks={periodTasks}
                  />
                )}

                {/* ---- UNCOVER ---- */}
                {uncoverRows.length > 0 && (
                  <TasksDayUncoverSection
                    title={getCustomLabel(grower ?? null, "uncover")}
                    rows={uncoverRows}
                    periodTasks={periodTasks}
                    uncoverColumns={uncoverColumns}
                    uncoverColumnOptions={[
                      { field: "batchNumber", label: "Batch" },
                      { field: "trays", label: "Trays" },
                      { field: "sowedOn", label: "Cycle Start Date" },
                      { field: "harvestDate", label: "For Harvest On" },
                    ]}
                  />
                )}

                {/* ---- HARVEST ---- */}
                {harvestRows.length > 0 && (
                  <TasksDayHarvestSection
                    title={getCustomLabel(grower ?? null, "harvest")}
                    rows={harvestRows}
                    periodTasks={periodTasks}
                    harvestColumns={harvestColumns}
                    harvestColumnOptions={[
                      { field: "batchNumber", label: "Batch" },
                      { field: "trays", label: "Trays" },
                      { field: "expectedYield", label: "Expected Yield" },
                      { field: "yieldActual", label: "Actual Yield" },
                      { field: "quantityNeeded", label: "Quantity Needed" },
                      ...(grower.Workflow === "advanced"
                        ? [{ field: "bufferExpected", label: "Buffer" }]
                        : []),
                      { field: "sowedOn", label: "Cycle Start Date" },
                    ]}
                  />
                )}

                {/* ---- PACKAGE ---- */}
                {packageRows.length > 0 && (
                  <TasksDayPackageSection
                    title={getCustomLabel(grower ?? null, "package")}
                    rows={packageRows}
                    periodTasks={periodTasks}
                    packageColumns={packageColumns}
                    packageColumnOptions={[
                      { field: "weight", label: "Weight" },
                      { field: "size", label: "Size" },
                    ]}
                  />
                )}

                {/* ---- NO TASKS ---- */}
                {soakRows.length === 0 &&
                  sowRows.length === 0 &&
                  blackoutRows.length === 0 &&
                  uncoverRows.length === 0 &&
                  harvestRows.length === 0 &&
                  packageRows.length === 0 && (
                    <Box pt={2}>
                      <Typography variant="body1">No tasks today.</Typography>
                    </Box>
                  )}
              </div>
            ) : (
              <TasksWeekView
                periodTasks={periodTasks}
                onClickDate={handleClickDate}
              />
            )}
          </>
        )}
        <Dialog
          open={taskBlockDialogOpen}
          onClose={() => setTaskBlockDialogOpen(false)}
          hideBackdrop={false}
          fullScreen={false}
          maxWidth={false}
          fullWidth
          sx={{
            "& .MuiDialog-container": {
              justifyContent: isMobile ? "flex-end" : "center",
              alignItems: isMobile ? "flex-end" : "center",
              m: 0,
            },
          }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: isMobile ? 12 : 4,
              borderTopRightRadius: isMobile ? 12 : 4,
              width: "100vw",
              maxWidth: isMobile ? "100vw" : 600,
              m: 0,
            },
          }}
        >
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Cannot Complete Task
            </Typography>

            <Typography gutterBottom>
              Tasks must be completed in order!
            </Typography>

            <br />

            {taskBlockData?.missingTasks?.length ? (
              <>
                <Typography>
                  {taskBlockData.missingTasks.length === 1
                    ? "Please complete the following task before continuing:"
                    : "Please complete the following task(s), in order, before continuing:"}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  {taskBlockData.missingTasks.map((raw, i) => {
                    const isEarliest = i === 0; // ✅ FIX

                    const label = raw
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase());

                    return (
                      <Typography
                        key={raw}
                        variant="h6"
                        align="center"
                        color={isEarliest ? "primary" : "black"}
                        sx={{
                          fontWeight: 400,
                          mt: i === 0 ? 1 : 0.5,
                          cursor: isEarliest ? "pointer" : "default",
                          textDecoration: isEarliest ? "underline" : "none",
                        }}
                        onClick={() => {
                          if (isEarliest && blockedTaskObject) {
                            navigateToTaskDay(raw, blockedTaskObject);
                            setTaskBlockDialogOpen(false);
                            setTimeout(() => window.scrollTo(0, 0), 200);
                          }
                        }}
                      >
                        {label}
                      </Typography>
                    );
                  })}
                </Box>
              </>
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setTaskBlockDialogOpen(false)} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <LoadingBackdrop open={loadingOtherData || loadingTasks || busy} />
        <UpgradePlanDialog
          open={openUpgradePlanDialog}
          onClickCancel={onClickCancelUpgradePlan}
        />
        <Dialog
          open={openUncompleteTaskPrompt}
          onClose={() => handleOnCloseConfirmUncompleteTask(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: "warning.main" }}>
            Warning
          </DialogTitle>

          <DialogContent>
            <Typography component="div">
              {uncompletingTaskType === "soak" ? (
                <>
                  Uncompleting this task will{" "}
                  <strong>
                    permanently delete the data entered for this soak task.
                  </strong>
                  <br />
                  <br />
                  This will <strong>not</strong> affect sow or any later tasks.
                  <br />
                  <br />
                  To make changes without losing downstream data, use the
                  (pencil) icon to <strong>edit this task.</strong>
                  <br />
                  <br />
                  Do you want to proceed with uncompleting this task?
                </>
              ) : uncompletingTaskType === "sow" ? (
                <>
                  Uncompleting this task will{" "}
                  <strong>
                    permanently delete the sow task, the batch number, and all
                    subsequent tasks in the workflow.
                  </strong>
                  <br />
                  <br />
                  This will reset the crop cycle and cannot be undone.
                  <br />
                  <br />
                  To make changes without losing downstream data, use the
                  (pencil) icon to <strong>edit this task.</strong>
                  <br />
                  <br />
                  Do you want to proceed with uncompleting this task?
                </>
              ) : (
                <>
                  Uncompleting this task will{" "}
                  <strong>
                    permanently delete data for this task and any subsequent
                    tasks in the workflow.
                  </strong>
                  <br />
                  <br />
                  To make changes without losing downstream data, use the
                  (pencil) icon to <strong>edit this task.</strong>
                  <br />
                  <br />
                  Do you want to proceed with uncompleting this task?
                </>
              )}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => handleOnCloseConfirmUncompleteTask(false)}>
              Cancel
            </Button>
            <Button
              color="warning"
              variant="contained"
              onClick={() => handleOnCloseConfirmUncompleteTask(true)}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={completeAllTasksResult !== null}
          onClose={() => setCompleteAllTasksResult(null)}
        >
          <DialogTitle>Bulk task update complete</DialogTitle>

          <DialogContent>
            <DialogContentText>
              {completeAllTasksResult?.completed ?? 0} task
              {(completeAllTasksResult?.completed ?? 0) === 1 ? "" : "s"}{" "}
              completed.
              <br />
              <br />
              {(completeAllTasksResult?.blocked ?? 0) > 0
                ? `${completeAllTasksResult?.blocked} task${
                    completeAllTasksResult?.blocked === 1 ? "" : "s"
                  } still need earlier tasks completed first.`
                : "Everything available was completed."}
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setCompleteAllTasksResult(null)}>OK</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openCompleteAllTasksDialog}
          onClose={() => setOpenCompleteAllTasksDialog(false)}
        >
          <DialogTitle>Complete all available tasks?</DialogTitle>

          <DialogContent>
            <DialogContentText>
              Ah yes, the legendary "check everything" button. Data managers
              everywhere just felt a disturbance in the Force.
              <br />
              <br />
              SeedLeaf will only complete tasks that are currently eligible.
              Tasks waiting on earlier steps will stay unchecked.
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenCompleteAllTasksDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCompleteAllAvailableTasks}
              disabled={busy}
            >
              Complete Available Tasks
            </Button>
          </DialogActions>
        </Dialog>
      </Page>

      {openTaskDialog && taskDialogData && (
        <Portal container={document.body}>
          <Dialog
            open={openTaskDialog}
            onClose={() => setOpenTaskDialog(false)}
            hideBackdrop={false}
            fullScreen={false}
            maxWidth={false}
            fullWidth
            sx={{
              "& .MuiDialog-container": {
                justifyContent: isMobile ? "flex-end" : "center",
                alignItems: isMobile ? "flex-end" : "center",
                m: 0,
              },
            }}
            PaperProps={{
              sx: {
                borderTopLeftRadius: isMobile ? 12 : 4,
                borderTopRightRadius: isMobile ? 12 : 4,
                width: "100vw",
                maxWidth: isMobile ? "100vw" : 600,
                m: 0,
              },
            }}
          >
            <Formik<TaskDialogFormValues>
              enableReinitialize
              innerRef={formikRef}
              initialValues={
                taskDialogData.task.completed && taskDialogData.completedTask
                  ? {
                      date: taskDialogData.completedTask.dateActual
                        ? DateTime.fromISO(
                            taskDialogData.completedTask.dateActual,
                          )
                        : DateTime.now().startOf("day"),

                      seedLots: taskDialogData.completedTask.seedLots
                        ? taskDialogData.completedTask.seedLots.map((v) =>
                            v.seedLot!.id.toString(),
                          )
                        : [],
                      pkgLots:
                        taskDialogData.completedTask.pkgLots?.map((l) =>
                          l.pkgLotId.toString(),
                        ) || [],
                      completedBy:
                        taskDialogData.completedTask.completedBy || "",
                      notes: taskDialogData.completedTask.notes || "",
                      yieldActual: taskDialogData.completedTask.yieldActual,
                      traysActual: taskDialogData.completedTask.traysActual,
                      packagesActual:
                        taskDialogData.completedTask.packagedQuantityActual,
                    }
                  : {
                      date: DateTime.fromISO(searchParams.get("date")!),
                      seedLots: shouldShowSeedLots()
                        ? (() => {
                            const activeLots =
                              taskDialogData.crop?.seed?.lots.filter(
                                (l) => l.archivedAt === null,
                              ) || [];

                            const defaultLot = activeLots.find(
                              (l) => l.isDefault,
                            );

                            if (defaultLot) {
                              return [defaultLot.id.toString()];
                            }

                            if (activeLots.length === 1) {
                              return activeLots.map((l) => l.id.toString());
                            }

                            return [""];
                          })()
                        : [],
                      pkgLots:
                        taskDialogData.type === "package"
                          ? [
                              (
                                (
                                  taskDialogData.task as PackageTask
                                ).productSize.pkg?.lots.find(
                                  (l) => l.isDefault && l.archivedAt === null,
                                ) ||
                                (
                                  taskDialogData.task as PackageTask
                                ).productSize.pkg?.lots.find(
                                  (l) => l.archivedAt === null,
                                )
                              )?.id.toString() || "",
                            ]
                          : [],
                      completedBy: defaultCompletedBy,
                      notes: "",
                      yieldActual:
                        taskDialogData.type === "harvest"
                          ? (taskDialogData.task as HarvestTask).expectedYield
                          : undefined,
                      traysActual:
                        taskDialogData.type !== "package"
                          ? (taskDialogData.task as CropTask).trays
                          : undefined,
                      packagesActual:
                        taskDialogData.type === "package"
                          ? (taskDialogData.task as PackageTask).quantity
                          : undefined,
                    }
              }
              validationSchema={taskDialogFormSchema}
              onSubmit={(v, helpers) => {
                if (
                  shouldShowSeedLots() &&
                  !v.seedLots[v.seedLots.length - 1]
                ) {
                  helpers.setFieldError(
                    `seedLots[${v.seedLots.length - 1}]`,
                    validationMessages.Required,
                  );
                  return;
                }
                onSubmitTaskDialogForm(v);
              }}
            >
              {(formProps: FormikProps<TaskDialogFormValues>) => {
                const selectedName = formProps.values.completedBy;

                return (
                  <Form noValidate autoComplete="off">
                    <Box
                      sx={{
                        width: "100%",
                        px: 2,
                        py: 2,
                      }}
                    >
                      <DialogTitle id="alert-dialog-title">
                        {taskDialogData.task.completed
                          ? "Edit Task"
                          : "Complete Task" +
                            ": " +
                            getTaskDialogSubTitle(taskDialogData)}
                      </DialogTitle>
                      <DialogContent>
                        <DialogContentText id="alert-dialog-description"></DialogContentText>

                        {/* Date Completed */}
                        <Box mt={2}>
                          <Field name="date">
                            {(fieldProps: FieldProps) => (
                              <MobileDatePicker
                                closeOnSelect
                                format="LLL d, yyyy"
                                value={
                                  fieldProps.field.value
                                    ? DateTime.fromISO(fieldProps.field.value)
                                    : null
                                }
                                onChange={(date) =>
                                  formProps.setFieldValue(
                                    "date",
                                    date && date.isValid ? date : null,
                                  )
                                }
                                label="Date Completed"
                                slotProps={{
                                  toolbar: { hidden: true },
                                  textField: {
                                    variant: "outlined",
                                    fullWidth: true,
                                    helperText:
                                      fieldProps.meta.touched &&
                                      fieldProps.meta.error,
                                    error:
                                      !!fieldProps.meta.error &&
                                      fieldProps.meta.touched,
                                    inputProps: { readOnly: true },
                                  },
                                }}
                              />
                            )}
                          </Field>
                        </Box>

                        {/* Seed Lots Field Array */}
                        {shouldShowSeedLots() && (
                          <FieldArray
                            name="seedLots"
                            render={(arrayHelpers) => (
                              <>
                                {formProps.values.seedLots.map((_, i) => (
                                  <div key={`seedLots[${i}]`}>
                                    <Field name={`seedLots[${i}]`}>
                                      {(fieldProps: FieldProps) => (
                                        <AppTextField
                                          {...fieldProps}
                                          select
                                          label={`Seed Lot ${i + 1}`}
                                        >
                                          {taskDialogData!
                                            .crop!.seed!.lots.filter(
                                              (l) => l.archivedAt === null,
                                            )
                                            .filter(
                                              (l) =>
                                                l.id.toString() ===
                                                  fieldProps.field.value ||
                                                !formProps.values.seedLots.includes(
                                                  l.id.toString(),
                                                ),
                                            )
                                            .map((l) => (
                                              <MenuItem
                                                key={l.id}
                                                value={l.id.toString()}
                                              >
                                                {l.number}
                                              </MenuItem>
                                            ))}
                                        </AppTextField>
                                      )}
                                    </Field>
                                  </div>
                                ))}

                                {formProps.values.seedLots.length <
                                  taskDialogData!.crop!.seed!.lots.filter(
                                    (l) => l.archivedAt === null,
                                  ).length && (
                                  <Button
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={() => arrayHelpers.push("")}
                                  >
                                    Add lot
                                  </Button>
                                )}

                                {formProps.values.seedLots.length > 1 && (
                                  <Button
                                    color="primary"
                                    startIcon={<RemoveIcon />}
                                    onClick={() => arrayHelpers.pop()}
                                  >
                                    Remove lot
                                  </Button>
                                )}
                              </>
                            )}
                          />
                        )}
                        {/* Completed By */}
                        <Box mt={2}>
                          <Autocomplete<GrowerUser, false, false, true>
                            options={users}
                            freeSolo
                            /* 🔑 Selected option (object or null) */
                            value={
                              users.find(
                                (u) =>
                                  u.Name.toLowerCase() ===
                                  formProps.values.completedBy.toLowerCase(),
                              ) ?? null
                            }
                            /* 🔑 Text inside the input */
                            inputValue={formProps.values.completedBy}
                            onInputChange={(_e, value) => {
                              formProps.setFieldValue("completedBy", value);
                            }}
                            onChange={(_e, value) => {
                              formProps.setFieldValue(
                                "completedBy",
                                typeof value === "string"
                                  ? value
                                  : (value?.Name ?? ""),
                              );
                            }}
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.Name
                            }
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  width="100%"
                                >
                                  <span>{option.Name}</span>

                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      usersApi
                                        .archiveGrowerUser(option.id)
                                        .then(() => {
                                          setUsers((prev) =>
                                            prev.filter(
                                              (u) => u.id !== option.id,
                                            ),
                                          );
                                        });
                                    }}
                                  >
                                    <ArchiveIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Completed By"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Box>

                        {/* Tray Count (non-package tasks) */}
                        {taskDialogData.type !== "package" && (
                          <FastField
                            name="traysActual"
                            type="number"
                            label="Tray Count"
                            component={AppTextField}
                          />
                        )}

                        {/* Yield (harvest only — advanced workflow) */}
                        {taskDialogData.type === "harvest" &&
                          grower.Workflow === "advanced" && (
                            <FastField
                              name="yieldActual"
                              type="number"
                              label={`Actual Yield (${unitSystemLabel(grower)})`}
                              component={AppTextField}
                            />
                          )}

                        {/* Package Count (package only) */}
                        {taskDialogData.type === "package" && (
                          <FastField
                            name="packagesActual"
                            type="number"
                            label="Actual Quantity"
                            component={AppTextField}
                          />
                        )}
                        {packagingPkg() && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              mt: 2,
                              alignItems: "flex-start",
                            }}
                          >
                            {" "}
                            <Box sx={{ flex: 1 }}>
                              <TextField
                                fullWidth
                                label="Package"
                                value={`${packagingPkg()?.name} ${packagingPkg()?.size}`}
                                InputProps={{
                                  readOnly: true,
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, mt: 0 }}>
                              <Field name="pkgLots[0]">
                                {(fieldProps: FieldProps) => (
                                  <TextField
                                    fullWidth
                                    select
                                    label="Package Lot"
                                    name="pkgLots[0]"
                                    value={formProps.values.pkgLots[0] || ""}
                                    onChange={formProps.handleChange}
                                  >
                                    <MenuItem value="">Select lot</MenuItem>
                                    {packagingPkg()
                                      ?.lots?.filter(
                                        (l) => l.archivedAt === null,
                                      )
                                      .map((l) => (
                                        <MenuItem
                                          key={l.id}
                                          value={l.id.toString()}
                                        >
                                          {l.number}
                                        </MenuItem>
                                      ))}
                                  </TextField>
                                )}
                              </Field>
                            </Box>
                          </Box>
                        )}
                        {/* Notes */}
                        <FastField name="notes">
                          {(fieldProps: FieldProps) => (
                            <AppTextField
                              {...fieldProps}
                              multiline
                              label="Notes"
                              rows={4}
                            />
                          )}
                        </FastField>
                      </DialogContent>

                      <DialogActions>
                        <Button
                          disabled={busy}
                          type="submit"
                          color="primary"
                          autoFocus
                        >
                          Ok
                        </Button>
                        <Button
                          disabled={busy}
                          onClick={() => setOpenTaskDialog(false)}
                        >
                          Cancel
                        </Button>
                      </DialogActions>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </Dialog>
        </Portal>
      )}
    </>
  );
}
